import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Droplets, Waves, PawPrint, Zap, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { saveProperty, unsaveProperty } from '../lib/api';
import { getListingUrl } from '../lib/seoUtils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PropertyCard({ 
  property, 
  showSaveButton = false,
  isSaved: initialSaved = false,
  onUnsave
}) {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(initialSaved);
  const [saving, setSaving] = useState(false);

  const {
    id,
    title,
    city_name,
    beds,
    baths,
    base_price_nightly,
    cover_image_thumb,
    has_pool,
    has_waterfront,
    is_pet_friendly,
    instant_book
  } = property;

  // Format price from cents to dollars
  const priceFormatted = (base_price_nightly / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  // Get image URL - handle both relative and absolute paths
  const imageUrl = cover_image_thumb 
    ? (cover_image_thumb.startsWith('http') ? cover_image_thumb : `${API_URL}${cover_image_thumb}`)
    : 'https://images.unsplash.com/photo-1753194931548-065891743746?w=600&h=400&fit=crop';

  const handleSaveClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.info('Please log in to save properties');
      navigate('/login');
      return;
    }

    setSaving(true);
    try {
      if (isSaved) {
        await unsaveProperty(id);
        setIsSaved(false);
        toast.success('Removed from saved');
        if (onUnsave) onUnsave(id);
      } else {
        await saveProperty(id);
        setIsSaved(true);
        toast.success('Property saved');
      }
    } catch (err) {
      toast.error('Failed to update saved status');
    } finally {
      setSaving(false);
    }
  };

  // Generate SEO-friendly listing URL
  const listingUrl = getListingUrl(property);

  return (
    <a 
      href={listingUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="property-card block relative"
      data-testid={`property-card-${id}`}
    >
      {/* Image */}
      <div className="relative aspect-property overflow-hidden">
        <img 
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {instant_book && (
            <span className="badge-instant flex items-center gap-1">
              <Zap className="w-3 h-3" />
              Instant Book
            </span>
          )}
        </div>

        {/* Save Button */}
        {showSaveButton && (
          <button
            onClick={handleSaveClick}
            disabled={saving}
            className={`absolute top-3 right-3 p-2 rounded-full transition-colors ${
              isSaved 
                ? 'bg-red-500 text-white hover:bg-red-600' 
                : 'bg-white/90 text-slate-600 hover:bg-white hover:text-red-500'
            }`}
            data-testid={`save-btn-${id}`}
            aria-label={isSaved ? 'Remove from saved' : 'Save property'}
          >
            <Heart className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="font-medium text-slate-900 truncate" title={title}>
              {title}
            </h3>
            <p className="text-sm text-slate-500 mt-1">{city_name}</p>
          </div>
        </div>

        {/* Property Details */}
        <div className="flex items-center gap-3 mt-3 text-sm text-slate-600">
          <span>{beds} bed{beds !== 1 ? 's' : ''}</span>
          <span className="text-slate-300">•</span>
          <span>{baths} bath{baths !== 1 ? 's' : ''}</span>
        </div>

        {/* Amenity Badges */}
        <div className="flex flex-wrap gap-2 mt-3">
          {has_pool && (
            <span className="badge-pool flex items-center gap-1">
              <Droplets className="w-3 h-3" />
              Pool
            </span>
          )}
          {has_waterfront && (
            <span className="badge-waterfront flex items-center gap-1">
              <Waves className="w-3 h-3" />
              Waterfront
            </span>
          )}
          {is_pet_friendly && (
            <span className="badge-pet flex items-center gap-1">
              <PawPrint className="w-3 h-3" />
              Pet Friendly
            </span>
          )}
        </div>

        {/* Price */}
        <div className="mt-4 pt-3 border-t border-slate-100">
          <p className="text-slate-900">
            <span className="text-lg font-semibold">{priceFormatted}</span>
            <span className="text-slate-500 text-sm"> / night</span>
          </p>
        </div>
      </div>
    </a>
  );
}
